import React, { useCallback, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { bool, func, any, objectOf } from 'prop-types';
import {
  Box,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  useToast,
} from '@chakra-ui/react';

import client from '../../../api/api';
import resizeImage from '../../../libs/resizer/imageResizer';
import Card from './Card';
import { LICENSE_OUTCOME, EDIT_MESSAGES } from '../AddTrucker/licenseOutcomeMessages';
// HD-1556: chrome del modal migrado a Chakra para alinear con "Agregar Humber".
// import ModalRegular from '../../../ui/modals';

const media = {
  tablet: '@media(min-width: 768px)',
  desktop: '@media(min-width: 1366px)',
};

const Row = styled.div`
  ${media.tablet} {
    margin: 20px auto;
    width: 90%;
  }

  ${media.desktop} {
    display: flex;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0 20px 0;

  img {
    width: 3pc;
    height: 3pc;
  }

  h1 {
    color: #ffbb0f;
    font-size: 20px;
    margin: 0;
  }

  ${media.tablet} {
    align-items: flex-start;

    img {
      width: 4pc;
      height: 4pc;
    }

    h1 {
      font-size: 1.6pc;
    }
  }
`;

const ErrorWrap = styled.div`
  width: 100%;
  background: red;

  p {
    color: white;
    padding: 10px;
  }
`;

const InfoWrap = styled.div`
  width: 100%;
  background: #1f2231;
  border-top: 1px solid #383d54;
  border-bottom: 1px solid #383d54;

  p {
    color: #ffbb0f;
    padding: 10px;
  }
`;

const SuccessInfoWrap = styled.div`
  width: 100%;
  background: #1f2231;
  border-top: 1px solid #2e7d32;
  border-bottom: 1px solid #2e7d32;
  display: flex;
  justify-content: center;

  p {
    color: #4caf50;
    padding: 10px;
    font-weight: 600;
  }
`;

const EditTruckerModal = ({
  trucker,
  premiumPhotos,
  openModal,
  setOpenModal,
  fetchTruckerList,
}) => {
  const { truck } = trucker;
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [celNumber, setCelNumber] = useState('');
  const [cbu, setCBU] = useState('');
  const [truckPatent, setTruckPatent] = useState('');
  const [trailerPatent, setTrailerPatent] = useState('');
  const [truckTypeSelected, setTruckTypeSelected] = useState({
    label: trucker?.truck?.truckType?.name,
  });
  const [bodyTypeSelected, setBodyTypeSelected] = useState({
    label: trucker?.truck?.trailer?.trailerType?.name,
  });
  const [licensePhoto, setLicensePhoto] = useState('');
  const [licensePhotoUpdated, setLicensePhotoUpdated] = useState('');
  const [licenseValidated, setLicenseValidated] = useState(false);
  const [validatingLicense, setValidatingLicense] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  // HD-1556: feedback visual por íconos reemplazado por el estado del modal Chakra.
  const [, setTruckerUpdated] = useState(false);
  const [, setPhotoUpdated] = useState(false);

  const [editTruckerInfo, setEditTruckerInfo] = useState(false);
  const [editBilingDataInfo, setEditBilingDataInfo] = useState(false);
  const [editTractorInfo, setEditTractorInfo] = useState(false);
  const [editTrailerInfo, setEditTrailerInfo] = useState(false);

  const errorRef = useRef(null);

  const truckerFields = [
    { label: 'Nombre y Apellido', value: trucker.name },
    { label: 'Celular', value: trucker.phoneNumber },
  ];

  const truckerInputFields = [
    {
      label: 'Celular',
      id: 'cel-number',
      type: 'number',
      defaultValue: trucker.phoneNumber,
      func: setCelNumber,
      errorMsg: '',
      disabled: false,
    },
  ];

  const billingFields = [
    {
      label: 'CUIT de facturación',
      value: trucker.billingData?.cuit || '',
    },
    {
      label: 'CBU',
      value: trucker.billingData?.cbu || '',
    },
  ];

  const billingInputFields = [
    {
      label: 'CBU',
      id: 'bank-account',
      type: 'text',
      defaultValue: trucker?.billingData?.cbu,
      func: setCBU,
      errorMsg: '',
      disabled: false,
    },
  ];

  const tractorFields = [
    { label: 'Patente', value: trucker?.truck?.plate || '' },
    { label: 'Tipo de camión', value: trucker?.truck?.truckType?.name || '' },
  ];

  const tractorInputFields = [
    {
      label: 'Patente',
      id: 'truck-patent',
      type: 'text',
      defaultValue: trucker?.truck?.plate,
      func: setTruckPatent,
      errorMsg: '',
      disabled: false,
    },
    {
      label: 'Tipo de camión',
      id: 'truck-type',
      type: 'select',
      defaultValue: truckTypeSelected,
      func: setTruckTypeSelected,
      errorMsg: '',
      disabled: false,
    },
  ];

  const trailerFields = [
    { label: 'Patente', value: trucker?.truck?.trailer?.plate || '' },
    { label: 'Carrocería', value: trucker?.truck?.trailer?.trailerType?.name || '' },
  ];

  const trailerInputFields = [
    {
      label: 'Patente',
      id: 'trailer-patent',
      type: 'text',
      defaultValue: trucker?.truck?.trailer?.plate,
      func: setTrailerPatent,
      errorMsg: '',
      disabled: false,
    },
    {
      label: 'Carrocería',
      id: 'truck-body-type',
      type: 'select',
      defaultValue: bodyTypeSelected,
      func: setBodyTypeSelected,
      errorMsg: '',
      disabled: false,
    },
  ];

  const syncTruckerVehicle = useCallback(async () => {
    if (!truck?.id) return;
    await client.endpoints.carrier.syncTruckerVehicle(trucker.id, truck.id);
  }, [trucker.id, truck?.id]);

  const updateTrucker = useCallback(async () => {
    try {
      const formData = new FormData();

      if (celNumber !== '') {
        formData.append('truckerCellPhone', celNumber);
      }
      if (truckPatent) {
        formData.append('truckPatent', truckPatent);
      }
      if (trailerPatent) {
        formData.append('trailerPatent', trailerPatent);
      }
      if (truckTypeSelected?._id) {
        // eslint-disable-next-line no-underscore-dangle
        formData.append('truckTypeId', truckTypeSelected._id);
      }
      if (bodyTypeSelected?._id) {
        // eslint-disable-next-line no-underscore-dangle
        formData.append('trailerBodyTypeId', bodyTypeSelected._id);
      }
      if (cbu) {
        formData.append('bankAccount', cbu);
      }

      await client.endpoints.carrier.editTrucker(formData, trucker.id);
      await syncTruckerVehicle();

      setTruckerUpdated(true);
    } catch (error) {
      console.error(error.response.data.message);
      throw new Error(`Error al actualizar chofer ${trucker.name}`);
    }
  }, [
    celNumber,
    truckPatent,
    trailerPatent,
    truckTypeSelected,
    bodyTypeSelected,
    cbu,
    syncTruckerVehicle,
  ]);

  const premiumImgList = useCallback(() => {
    const photoUpdateList = [];

    if (licensePhotoUpdated !== '') {
      photoUpdateList.push({
        photoTag: 'Foto registro',
        photoName: 'truckerLicensePhoto',
        photoFile: licensePhotoUpdated,
      });
    }
    return photoUpdateList;
  }, [licensePhotoUpdated]);

  const updatePhoto = useCallback(
    async img => {
      try {
        const resizedImage = await resizeImage({ file: img.photoFile });

        const formData = new FormData();
        formData.append('photoName', img.photoName);
        formData.append('photoFile', resizedImage);

        await client.endpoints.carrier.editTruckerPhotos(formData, truck.id);

        setPhotoUpdated(true);
      } catch (error) {
        console.error(error);
        throw new Error(`Error al actualizar foto: ${img.photoTag} (${img.photoName})`);
      }
    },
    [resizeImage]
  );

  const close = () => {
    fetchTruckerList();
    setOpenModal(false);
  };

  const handleLicensePhotoChange = async file => {
    setLicensePhotoUpdated(file);
    setLicenseValidated(false);
    setSuccessMsg('');

    if (!file) return;

    try {
      setValidatingLicense(true);
      setErrorMsg('');
      const res = await client.endpoints.carrier.validateTruckerLicensePreview(
        trucker.cuit,
        file
      );
      if (res.status === 200 && res.data?.valid) {
        setLicenseValidated(true);
        setErrorMsg('');
        setSuccessMsg('Validación correcta');
      } else {
        setLicenseValidated(false);
        setErrorMsg('El registro no es válido o no coinciden los datos. Validaremos manualmente tus datos.');
      }
    } catch (error) {
      setLicenseValidated(false);
      setErrorMsg('El registro no es válido o no coinciden los datos. Validaremos manualmente tus datos.');
    } finally {
      setValidatingLicense(false);
    }
  };

  const handleRemoveLicensePhoto = () => {
    setLicensePhotoUpdated('');
    setLicenseValidated(false);
    setSuccessMsg('');
    setErrorMsg('');
  };

  const updateChanges = useCallback(async () => {
    if (validatingLicense) {
      setErrorMsg('Esperá a que termine la validación del registro');
      return;
    }

    try {
      setLoading(true);

      const photoListForUpdate = premiumImgList();

      if (photoListForUpdate.length > 0) {
        const promises = photoListForUpdate.map(async photo => {
          await updatePhoto(photo);
        });
        await Promise.all(promises);
      }

      if (
        celNumber ||
        truckPatent ||
        trailerPatent ||
        truckTypeSelected?._id ||
        bodyTypeSelected?._id ||
        cbu
      ) {
        await updateTrucker();
      }

      let licenseOutcome = null;
      if (licensePhotoUpdated) {
        await syncTruckerVehicle();
        await client.endpoints.carrier.syncTruckerLicensePhoto(trucker.id, truck.id);
        // Validación completa en servidor siempre que hay foto nueva: si falla, el camionero
        // pierde premium; si pasa (aunque el preview hubiera fallado), recupera premium.
        try {
          await client.endpoints.carrier.validateTruckerLicense(
            trucker.id,
            trucker.cuit,
            licensePhotoUpdated,
            truck.id
          );
          licenseOutcome = LICENSE_OUTCOME.PREMIUM_OK;
        } catch (err) {
          console.error('Error al validar registro / estado premium', err);
          licenseOutcome = LICENSE_OUTCOME.LICENSE_INVALID;
        }
      }

      setLoading(false);

      if (licenseOutcome) {
        toast({
          title: licenseOutcome === LICENSE_OUTCOME.PREMIUM_OK ? 'Éxito' : 'Atención',
          description: EDIT_MESSAGES[licenseOutcome],
          status: licenseOutcome === LICENSE_OUTCOME.PREMIUM_OK ? 'success' : 'warning',
          duration: 8000,
          isClosable: true,
        });
      }

      close();
    } catch (error) {
      setLoading(false);
      setErrorMsg(error.message);
    }
  }, [
    celNumber,
    cbu,
    truckPatent,
    trailerPatent,
    truckTypeSelected,
    bodyTypeSelected,
    premiumImgList,
    licensePhotoUpdated,
    validatingLicense,
    syncTruckerVehicle,
  ]);

  const checkStatus = () => {
    const hasPhoto = !!premiumPhotos.truckerLicensePhoto;
    setLicensePhoto({
      imgURI: premiumPhotos.truckerLicensePhoto,
      imgStatus: hasPhoto
        ? true
        : (premiumPhotos.confirmedFrontLicensePhoto !== null &&
           premiumPhotos.confirmedFrontLicensePhoto !== ''
            ? premiumPhotos.confirmedFrontLicensePhoto
            : null),
      expiration: premiumPhotos.truckerLicenseExpiration
        ? moment(premiumPhotos.truckerLicenseExpiration).format('DD/MM/YYYY')
        : ' - ',
    });
  };

  useEffect(() => {
    checkStatus();
  }, []);

  useEffect(() => {
    if (errorMsg && errorRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errorMsg, errorRef]);

  useEffect(() => {
    if (errorMsg === 'El registro no es válido o no coinciden los datos. Validaremos manualmente tus datos.') {
      const timeoutId = setTimeout(() => setErrorMsg(''), 4000);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [errorMsg]);

  return (
    <Modal
      isOpen={openModal}
      onClose={close}
      size="6xl"
      scrollBehavior="inside"
      blockScrollOnMount
      preserveScrollBarGap
    >
      <ModalOverlay />
      <ModalContent maxH="90vh" bg="#31354e" position="relative">
        {loading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
            zIndex={10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
          >
            <Spinner thickness="4px" speed="0.65s" emptyColor="#383d54" color="#ffbb0f" size="xl" />
          </Box>
        )}
        <ModalHeader color="white" borderBottom="1px solid #1f2231">
          Editar Humber
        </ModalHeader>
        <ModalCloseButton
          color="white"
          _hover={{ bg: 'whiteAlpha.200' }}
          isDisabled={loading}
        />
        <ModalBody overflowY="auto" p={4}>
          <Header>
            <img src="../../../images/icons/super-premium.png" alt="super-premium" />
            <h1>Hacé premium a tu camionero con la foto del registro</h1>
          </Header>
          {validatingLicense && (
            <InfoWrap>
              <p>Validando registro...</p>
            </InfoWrap>
          )}
          {successMsg && !validatingLicense && !errorMsg && (
            <SuccessInfoWrap>
              <p>{successMsg}</p>
            </SuccessInfoWrap>
          )}
          {errorMsg ? (
            <ErrorWrap>
              <p ref={errorRef}>{errorMsg}</p>
            </ErrorWrap>
          ) : (
            ''
          )}
          <>
            <Row>
              {licensePhoto ? (
                <Card
                  title="Chofer"
                  fields={truckerFields}
                  setEditCardInfo={setEditTruckerInfo}
                  editCardInfo={editTruckerInfo}
                  inputFields={truckerInputFields}
                  img={licensePhoto}
                  uploadImgBtnLabel="Registro"
                  billingData={false}
                  setImg={handleLicensePhotoChange}
                  onRemove={handleRemoveLicensePhoto}
                />
              ) : (
                ''
              )}
              <Card
                title="Datos de facturación"
                fields={billingFields}
                setEditCardInfo={setEditBilingDataInfo}
                editCardInfo={editBilingDataInfo}
                inputFields={billingInputFields}
                billingData
              />
            </Row>
            <Row>
              <Card
                title="Tractor"
                fields={tractorFields}
                setEditCardInfo={setEditTractorInfo}
                editCardInfo={editTractorInfo}
                inputFields={tractorInputFields}
                billingData={false}
                showDocumentStatus={false}
              />
              <Card
                title="Remolque"
                fields={trailerFields}
                setEditCardInfo={setEditTrailerInfo}
                editCardInfo={editTrailerInfo}
                inputFields={trailerInputFields}
                billingData={false}
                showDocumentStatus={false}
              />
            </Row>
          </>
        </ModalBody>
        <ModalFooter bg="#1f2231" borderTop="1px solid #383d54">
          <Button variant="ghost" onClick={close} color="white" mr={3} isDisabled={loading}>
            Cancelar
          </Button>
          <Button
            colorScheme="yellow"
            onClick={() => {
              setErrorMsg('');
              updateChanges();
            }}
            isDisabled={loading}
          >
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

EditTruckerModal.propTypes = {
  trucker: objectOf(any),
  setOpenModal: func.isRequired,
  fetchTruckerList: func.isRequired,
  openModal: bool.isRequired,
  premiumPhotos: objectOf(any),
};

EditTruckerModal.defaultProps = {
  trucker: {},
  premiumPhotos: {},
};

export default EditTruckerModal;
